import * as React from 'react';

import {
  Button,
  cn,
  IconButton,
  Popover,
  PopoverTrigger,
  TextArea,
} from '@rematter/ui-web';
import { LucideEdit } from 'lucide-react';

export interface NotesFieldCellProps {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  title?: string;
  saveLabel?: string;
  discardLabel?: string;
}

export const NotesFieldCell: React.FC<NotesFieldCellProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  title = 'Notes',
  saveLabel = 'Save',
  discardLabel = 'Discard',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [localNotes, setLocalNotes] = React.useState(value ?? '');

  const handleSave = React.useCallback(() => {
    onChange(localNotes);
    setIsOpen(false);
  }, [localNotes, onChange]);

  const handleDiscard = React.useCallback(() => {
    setLocalNotes(value ?? '');
    setIsOpen(false);
  }, [value]);

  React.useEffect(() => {
    setLocalNotes(value ?? '');
  }, [value]);

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        icon={<LucideEdit size={18} />}
        variant="outline"
        className={cn('size-[40px]', className)}
        color={isOpen ? 'primary' : undefined}
        aria-label={title}
        onPress={() => setIsOpen(!isOpen)}
        isDisabled={disabled}
      />

      <Popover className="border-none">
        <div className="no-scrollbar flex min-h-[150px] w-[330px] flex-col items-center gap-3 overflow-auto rounded-md border-[1px] border-neutral/10 bg-white p-4 shadow-lg">
          <div className="flex w-full justify-start text-sm text-neutral">
            {title}
          </div>
          <TextArea
            value={localNotes}
            className="h-full w-full [&_textarea]:min-h-[80px]"
            placeholder={placeholder}
            onChange={setLocalNotes}
          />
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline" onPress={handleDiscard}>
              {discardLabel}
            </Button>
            <Button variant="solid" className="w-[75px]" onPress={handleSave}>
              {saveLabel}
            </Button>
          </div>
        </div>
      </Popover>
    </PopoverTrigger>
  );
};
